import { useState, useRef, useCallback, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Input,
  Radio,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
  Alert,
} from "antd";
import {
  AudioOutlined,
  SendOutlined,
  StopOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { useVoiceRecorder } from "../hooks/useVoiceRecorder";
import { useAuthStore } from "../stores/authStore";
import { ChatBubble, type ChatMessage } from "../components/ChatBubble";
import { interactApi } from "../api/interact";
import { v4 as uuidv4 } from "uuid";

const { Text } = Typography;

function playAudioBase64(base64: string, format: string) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: `audio/${format}` });
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio
    .play()
    .catch(() => message.warning('浏览器阻止了自动播放，请点击"播放回复"'));
  audio.onended = () => URL.revokeObjectURL(url);
}

export default function VoiceSimulator() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const {
    status: recorderStatus,
    audioBlob,
    startRecording,
    stopRecording,
    resetRecording,
    error: recorderError,
  } = useVoiceRecorder();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [textInput, setTextInput] = useState("");
  const [forceBackend, setForceBackend] = useState<"auto" | "local" | "remote">(
    "auto",
  );
  const [withAudio, setWithAudio] = useState(true);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addMessage = (msg: ChatMessage) => {
    setMessages((prev) => [...prev, msg]);
  };

  const handleSendText = useCallback(async () => {
    if (!textInput.trim()) return;

    const userMsg: ChatMessage = {
      id: uuidv4(),
      role: "user",
      inputType: "text",
      content: textInput.trim(),
      timestamp: new Date(),
    };
    addMessage(userMsg);
    const currentText = textInput.trim();
    setTextInput("");
    setSending(true);

    try {
      const res = await interactApi.text({
        text: currentText,
        force_backend: forceBackend,
        with_audio: withAudio,
      });
      const aiMsg: ChatMessage = {
        id: res.session_id,
        role: "ai",
        inputType: "text",
        content: res.response_text,
        backendUsed: res.backend_used,
        audioBase64: res.audio_base64,
        audioFormat: res.audio_format,
        latencyMs: res.latency_ms,
        timestamp: new Date(),
      };
      addMessage(aiMsg);
      if (withAudio && res.audio_base64) {
        playAudioBase64(res.audio_base64, res.audio_format);
      }
    } catch (err: unknown) {
      message.error((err as Error).message);
    } finally {
      setSending(false);
    }
  }, [textInput, forceBackend, withAudio]);

  const handleSendVoice = useCallback(async () => {
    if (!audioBlob) return;

    const userMsg: ChatMessage = {
      id: uuidv4(),
      role: "user",
      inputType: "voice",
      content: "🎤 语音消息（识别中...）",
      timestamp: new Date(),
    };
    addMessage(userMsg);
    setSending(true);

    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");
      formData.append("force_backend", forceBackend);
      formData.append("language", "zh");

      const res = await interactApi.voice(formData);

      // 更新用户消息，加上 ASR 识别文本
      setMessages((prev) =>
        prev.map((m) =>
          m.id === userMsg.id
            ? { ...m, content: res.text_input, asrText: res.text_input }
            : m,
        ),
      );

      const aiMsg: ChatMessage = {
        id: res.session_id,
        role: "ai",
        inputType: "voice",
        content: res.response_text,
        backendUsed: res.backend_used,
        audioBase64: res.audio_base64,
        audioFormat: res.audio_format,
        latencyMs: res.latency_ms,
        timestamp: new Date(),
      };
      addMessage(aiMsg);
      resetRecording();
      if (withAudio && res.audio_base64) {
        playAudioBase64(res.audio_base64, res.audio_format);
      }
    } catch (err: unknown) {
      message.error((err as Error).message);
    } finally {
      setSending(false);
    }
  }, [audioBlob, forceBackend, withAudio, resetRecording]);

  const canSendVoice = !!audioBlob && !sending;
  const canSendText = textInput.trim().length > 0 && !sending;

  // ── 未登录提示 ─────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <Alert
        type="warning"
        message="请先登录"
        description="语音模拟器（中枢模式）需要登录后才能使用。请先登录后重试。"
        showIcon
      />
    );
  }

  return (
    <div>
      <Row gutter={16}>
        {/* 左侧：配置面板 */}
        <Col xs={24} lg={8}>
          {/* 中枢标识 */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🎙️</div>
              <Text strong>智能中枢</Text>
              <Text
                type="secondary"
                style={{ fontSize: 12, display: "block", marginTop: 8 }}
              >
                直接与中枢对话，控制设备
              </Text>
            </div>
          </Card>

          <Card title="推理配置" size="small">
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  后端模式
                </Text>
                <Radio.Group
                  value={forceBackend}
                  onChange={(e) => setForceBackend(e.target.value)}
                  style={{ display: "block", marginTop: 6 }}
                >
                  <Space direction="vertical">
                    <Radio value="auto">自动（根据复杂度路由）</Radio>
                    <Radio value="local">强制本地模型</Radio>
                    <Radio value="remote">强制远程 API</Radio>
                  </Space>
                </Radio.Group>
              </div>
              <div>
                <Radio
                  checked={withAudio}
                  onChange={(e) => setWithAudio(e.target.checked)}
                >
                  返回 TTS 语音
                </Radio>
              </div>
            </Space>
          </Card>
        </Col>

        {/* 右侧：对话区 */}
        <Col xs={24} lg={16}>
          <Card
            title="与中枢对话"
            style={{ height: "100%" }}
            extra={
              <Button
                size="small"
                icon={<ClearOutlined />}
                onClick={() => setMessages([])}
                disabled={messages.length === 0}
              >
                清空
              </Button>
            }
          >
            {/* 对话记录 */}
            <div
              style={{
                height: 380,
                overflowY: "auto",
                padding: "8px 4px",
                background: "#fafafa",
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              {messages.length === 0 ? (
                <div
                  style={{ textAlign: "center", marginTop: 80, color: "#bbb" }}
                >
                  开始对话 — 录音或输入文字
                </div>
              ) : (
                messages.map((msg) => (
                  <ChatBubble
                    key={msg.id}
                    message={msg}
                    onPlayAudio={playAudioBase64}
                  />
                ))
              )}
              {sending && (
                <div style={{ textAlign: "center", padding: 8 }}>
                  <Spin size="small" />{" "}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    AI 思考中...
                  </Text>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <Divider style={{ margin: "8px 0" }} />

            {/* 语音录制区 */}
            <Card size="small" title="语音输入" style={{ marginBottom: 12 }}>
              {recorderError && (
                <Alert
                  type="error"
                  message={recorderError}
                  style={{ marginBottom: 8 }}
                />
              )}
              <Space wrap>
                {recorderStatus === "idle" && !audioBlob && (
                  <Button
                    type="primary"
                    icon={<AudioOutlined />}
                    onMouseDown={startRecording}
                    onMouseUp={stopRecording}
                    onTouchStart={startRecording}
                    onTouchEnd={stopRecording}
                  >
                    按住录音
                  </Button>
                )}
                {recorderStatus === "recording" && (
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={stopRecording}
                  >
                    停止录音
                  </Button>
                )}
                {audioBlob && (
                  <>
                    <Tag color="blue">
                      {(audioBlob.size / 1024).toFixed(1)} KB 已录制
                    </Tag>
                    <Button
                      type="primary"
                      loading={sending}
                      disabled={!canSendVoice}
                      onClick={handleSendVoice}
                      icon={<SendOutlined />}
                    >
                      发送语音
                    </Button>
                    <Button onClick={resetRecording}>重录</Button>
                  </>
                )}
              </Space>
            </Card>

            {/* 文本输入区 */}
            <Space.Compact style={{ width: "100%" }}>
              <Input
                placeholder="输入文字与中枢对话..."
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onPressEnter={canSendText ? handleSendText : undefined}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSendText}
                loading={sending}
                disabled={!canSendText}
              >
                发送
              </Button>
            </Space.Compact>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
